/**
 * @JUNOFileUpload 
 * - standalone secure object upload procedure to AWS S3 /bucket/folder
 * - single function call `beginUploadProcess`
 
 ** ********** VERIFY THE FOLLOWING **************** 
 * ! -- `S3_IMAGEBUCKET_LOCATION` in SMC set correctly
 * ! -- `s3_url` in Api Configs set correctly
 * ! -- `s3_upload_folder` in Api Configs set correctly
 * ! -- Folder must already exist in AWS S3 bucket. 
 *      ? The AWS sdk does not have CREATE or DELETE permissions for security purposes
 * 
 
 ***************** Usage **********************
 * ? beginUploadProcess takes a parameter of type FileUploadParams and returns void
  let validationResults = FileUploadHelper.validateFileAttributes(file);
  if (validationResults.isvalid){
      JUNOFileUpload.beginUploadProcess(aParams: FileUploadParams);
  }

  **** Helper Class ****
  @FileUploadHelper

 *
 */
import AwsApi from '../../../utils/AwsFrontend/AwsApi';
import { FontAwesomeConfigurations } from '../../../utils/Configuration/FontAwesomeConfiguration';
import { GlobalHelperFunctions } from '../../../utils/GlobalHelperFunctions';
import './style.scss';

export interface FileUploadParams {
  use_platform_bucket: boolean;
  contentId: number;
  displayname: string;
  file: File;
  filename: string;
  cancel_callback: Function;
  ok_callback: Function;
  progress_cancel_callback: Function;
  done_callback: Function;
}

export default class JUNOFileUpload {
  private static _fileUploadUX: JQuery<HTMLElement>;
  private static _uploadParams: FileUploadParams;
  private static _uploadAbortHook: Function;

  private static _okBtn: BaseCSSButton;
  private static _cancelBtn: BaseCSSButton;
  private static _errorBtn: BaseCSSButton;
  private static _doneBtn: BaseCSSButton;
  public static isUploadingFile = false;

  public static beginUploadProcess(uploadParams: FileUploadParams): void {
    if (this._fileUploadUX) {
      let confirmTitle = CrowdHub_App.LANG.FILE_UPLOAD_ONEATATIME_TITLE;
      let confirmMessage = CrowdHub_App.LANG.FILE_UPLOAD_ONEATATIME_MESSAGE;
      var confirmPopup = new ConfirmPopupPanel();
      confirmPopup.showOkConfirm(confirmTitle, confirmMessage, () => {
        if (this._uploadParams.cancel_callback) {
          this._uploadParams.cancel_callback();
        }
      });
      this._uploadParams.cancel_callback = null;
      return;
    }

    this._uploadParams = uploadParams;

    this._buildUx();
    this._buildButtons();
    this._errorBtn.mDiv.hide();
    $('body').append(this._fileUploadUX);
    GlobalHelperFunctions.setupDragDrop(this._fileUploadUX[0]);
  }

  private static _buildButtons(): void {
    this._cancelBtn = new BaseCSSButton();
    this._cancelBtn.initialize({
      id: 'upload_cancel_button',
      textStyle: '',
      buttonStyle: 'standard_button_outline',
      text: 'Cancel',
      click: () => {
        if (this._uploadParams.cancel_callback) {
          this._uploadParams.cancel_callback();
          this._destroy();
        } else {
          // we've killed the cancel already meaning we have already started to upload
          this._progressCanceled();
        }
      },
      scope: this,
      showLoader: false,
      container: this._fileUploadUX.find('.file_upload_progress_buttons'),
    });
    this._okBtn = new BaseCSSButton();
    this._okBtn.initialize({
      id: 'upload_ok_button',
      textStyle: '',
      buttonStyle: 'standard_button_filled',
      text: 'Confirm',
      click: () => {
        this._fileUploadUX.css('top', 'unset');
        this._fileUploadUX.animate(
          {
            left: '25px',
            bottom: '25px',
          },
          500,
        );
        if (this._uploadParams.ok_callback) {
          this._uploadParams.ok_callback();
        }
        // we are taking over the cancel for our own opportunities
        this._uploadParams.cancel_callback = null;
        this._startFileUploadProcess();
        this._okBtn.mDiv.hide();

        this._fileUploadUX.find('.file_upload_show_message').trigger('click');
      },
      scope: this,
      showLoader: false,
      container: this._fileUploadUX.find('.file_upload_progress_buttons'),
    });
    this._errorBtn = new BaseCSSButton();
    this._errorBtn.initialize({
      id: 'upload_error_button',
      textStyle: '',
      buttonStyle: 'standard_button_filled',
      text: 'Close',
      click: () => {
        this._destroy();
      },
      scope: this,
      showLoader: false,
      container: this._fileUploadUX.find('.file_upload_progress_buttons'),
    });
  }

  private static _startFileUploadProcess(): void {
    this._fileUploadUX.find('.file_upload_initial_info').hide();
    // TODO do we want to add size uploaded as well
    let struct = $(
      HGEN.tree({
        style: '',
        children: [
          {
            style: 'upload_progress_container',
            children: [
              {
                style: 'upload_progress_container_message',
                interior: CrowdHub_App.LANG.FILE_UPLOAD_PROGRESS_MESSAGE,
              },
              { style: 'upload_progress_container_percentage' },
            ],
          },
          { id: 'upload_progress_bar' },
        ],
      }),
    );

    this._fileUploadUX.addClass('flexy');
    this._fileUploadUX.find('.file_upload_progress_buttons_message').html(struct);

    var bar = new ProgressBar.Line('#upload_progress_bar', {
      // Stroke color
      color: CrowdHub_App.CONFIG.PROGRESS_BAR_STROKE_COLOR,
      // Width of the stroke
      strokeWidth: CrowdHub_App.CONFIG.PROGRESS_BAR_STROKE_WIDTH,
      // Background color
      trailColor: CrowdHub_App.CONFIG.PROGRESS_BAR_BG_COLOR,
      easing: 'easeInOut',
      duration: 100,
    });

    bar.animate(0);

    let fileMetadata = {
      base_url: CrowdHub_App.CONFIG.BASE_URL,
      content_id: this._uploadParams.contentId.toString(),
      platform: JSON.stringify(window.PLATFORM_DATA),
      user_id: App.INSTANCE.CurrentUser.Id.toString(),
    };

    // we automatically add a time stamp to the end of every file so that we maintain distinct file names
    let rightNowStamp = moment.now();
    let re = /(?:\.([^.]+))?$/;
    let fileNameExt = re.exec(this._uploadParams.file.name)[1];
    let fileNameParts = this._uploadParams.file.name.split(`.${fileNameExt}`);
    let fileNameNoExt = fileNameParts[0];
    this._uploadParams.filename = `${fileNameNoExt}_${rightNowStamp}.${fileNameExt}`;

    AwsApi.setupS3Client(this._uploadParams.use_platform_bucket)
      .then(() => {
        let uploadObjectParams = {
          use_platform_bucket: this._uploadParams.use_platform_bucket,
          upload_object: this._uploadParams.file,
          object_name: this._uploadParams.filename,
          metadata: fileMetadata,
          progress_callback: progress => {
            bar.animate(progress / 100);
            this._animateProgress(progress);
            this.isUploadingFile = true;
          },
          done_callback: (error, results) => {
            this.isUploadingFile = false;
            if (error) {
              this._handleUploadError(error);
            } else {
              this._cancelBtn.mDiv.hide();
              this._fileUploadUX.find('.file_upload_show_message').removeClass('active');
              this._fileUploadUX.find('.file_upload_progress_message').removeClass('active');

              struct.find('.upload_progress_container_message').text(CrowdHub_App.LANG.FILE_UPLOAD_COMPLETE_MESSAGE);
              struct.find('.upload_progress_container_loader').hide();

              AwsApi.updateJunoDbWithFileData({
                content_id: this._uploadParams.contentId,
                upload_name: this._uploadParams.file.name,
                upload_size_bytes: this._uploadParams.file.size,
                upload_source: results.Location,
                upload_type: this._uploadParams.file.type,
                upload_results: JSON.stringify(results),
              });

              this._doneBtn = new BaseCSSButton();
              this._doneBtn.initialize({
                id: 'upload_done_button',
                textStyle: '',
                buttonStyle: 'standard_button_filled',
                text: 'Ok',
                click: () => {
                  if (this._uploadParams.done_callback) {
                    this._uploadParams.done_callback(results);
                  }
                  this._destroy();
                },
                scope: this,
                showLoader: false,
                container: this._fileUploadUX.find('.file_upload_progress_buttons'),
              });
              this._doneBtn.mDiv.show();
            }
          },
        };

        this._uploadAbortHook = AwsApi.uploadObjectToS3(uploadObjectParams);
      })
      .catch(error => {
        console.error(error);
      });
  }

  private static _animateProgress(progress: number): void {
    this._fileUploadUX.find('.upload_progress_container_percentage').text(`${progress}%`);
  }

  private static _buildUx(): void {
    let struct = {
      style: 'juno_file_upload',
      children: [
        {
          style: 'file_upload_show_message',
          children: [
            {
              style: 'file_upload_icon info',
              interior: HGEN.faicon({
                style: FontAwesomeConfigurations.ICON_CIRCLE_INFO,
              }),
            },
            {
              style: 'file_upload_icon minus',
              interior: HGEN.faicon({
                style: FontAwesomeConfigurations.ICON_MINUS,
              }),
            },
            {
              style: 'file_upload_progress_message panel',
              children: [
                {
                  style: 'file_upload_progress_message_title',
                  interior: CrowdHub_App.LANG.FILE_UPLOAD_INITIAL_TEXT_TITLE,
                },
                {
                  style: 'file_upload_progress_message_message',
                  interior: CrowdHub_App.LANG.FILE_UPLOAD_INITIAL_TEXT_MESSAGE,
                },
              ],
            },
          ],
        },
        {
          style: 'file_upload_initial_info',
          children: [
            {
              style: 'file_upload_header',
              interior: CrowdHub_App.LANG.FILE_UPLOAD_HEADER,
            },
            {
              style: 'file_upload_body',
              children: [
                {
                  style: 'file_upload_name',
                  children: [
                    {
                      style: 'label',
                      interior: 'Display Name',
                    },
                    {
                      style: 'value',
                      interior: this._uploadParams.displayname,
                    },
                  ],
                },
                {
                  style: 'file_upload_name',
                  children: [
                    {
                      style: 'label',
                      interior: 'File Name',
                    },
                    {
                      style: 'value',
                      interior: this._uploadParams.filename,
                    },
                  ],
                },
                {
                  style: 'file_upload_details',
                  children: [
                    {
                      style: 'file_upload_size',
                      children: [
                        {
                          style: 'label',
                          interior: 'File Size',
                        },
                        {
                          style: 'value',
                          interior: GlobalHelperFunctions.generateHumanFileSize(this._uploadParams.file.size),
                        },
                      ],
                    },
                    {
                      style: 'file_upload_type',
                      children: [
                        {
                          style: 'label',
                          interior: 'File Type',
                        },
                        {
                          style: 'value',
                          interior: this._uploadParams.file.type,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          style: 'file_upload_progress_buttons_message',
          interior: CrowdHub_App.LANG.FILE_UPLOAD_START_MESSAGE,
        },
        { style: 'file_upload_progress_buttons' },
      ],
    };

    this._fileUploadUX = $(HGEN.tree(struct));

    this._fileUploadUX.find('.file_upload_show_message').on('click', function () {
      var panel = $('.file_upload_progress_message')[0];
      if (panel.style.maxHeight) {
        panel.style.maxHeight = null;
        $(panel).removeClass('active');
        this.classList.remove('active');
      } else {
        panel.style.maxHeight = panel.scrollHeight + 'px';
        $(panel).addClass('active');
        this.classList.add('active');
      }
    });
  }

  private static _handleUploadError(aError): void {
    if (String(aError).search('Request aborted by user') > -1) {
      // the user canceled
      if (this._uploadParams.progress_cancel_callback) {
        this._uploadParams.progress_cancel_callback();
      }
      return;
    }

    this._fileUploadUX.hide();
    let confirmPopup = new ConfirmPopupPanel();

    if (this._uploadParams.progress_cancel_callback) {
      this._uploadParams.progress_cancel_callback();
    }

    this._cancelBtn.mDiv.hide();
    this._errorBtn.mDiv.show();
    this._fileUploadUX.find('.upload_progress_container_message').text('Upload failed!');
    this._fileUploadUX.find('.upload_progress_container_loader').hide();

    confirmPopup.showOkConfirm(
      CrowdHub_App.LANG.FILE_UPLOAD_FAILED_TITLE,
      CrowdHub_App.LANG.FILE_UPLOAD_FAILED_MESSAGE + aError.message,
      () => {
        this._fileUploadUX.show();
      },
    );
  }

  private static _progressCanceled(): void {
    this._fileUploadUX.hide();
    let confirmPopup = new ConfirmPopupPanel();

    var okButton = {
      buttonCallback: () => {
        if (this._uploadParams.progress_cancel_callback) {
          this._uploadParams.progress_cancel_callback();
        }
        this._uploadAbortHook();
        this._destroy();
      },
      buttonId: 'ok_button',
      buttonStyle: 'standard_button_filled',
      buttonText: 'Ok',
    };
    var cancelButton = {
      buttonCallback: () => {
        this._fileUploadUX.show();
      },
      buttonId: 'cancel_button',
      buttonStyle: 'standard_button_outline margin_bottom_none',
      buttonText: 'Cancel',
    };

    confirmPopup.showMultiButtonConfirm(
      CrowdHub_App.LANG.FILE_UPLOAD_CANCEL_TITLE,
      CrowdHub_App.LANG.FILE_UPLOAD_CANCEL_MESSAGE,
      [okButton, cancelButton],
      {
        styles: {
          titleAlignment: 'text_left',
          subTitleAlignment: 'text_left',
        },
      },
    );
  }

  private static _destroy(): void {
    GlobalHelperFunctions.removeDragDrop(this._fileUploadUX[0]);
    this._okBtn = null;
    this._cancelBtn = null;
    this._uploadParams = null;
    this._fileUploadUX.remove();
    this._fileUploadUX = null;
  }
}
